import { useEffect, useState } from "react";
import {
    Plus, X, Edit3, Trash2, Search, ChevronLeft, ChevronRight,
    Upload, Download, Store, User, Phone, Mail, CheckCircle,
    XCircle, AlertCircle, FileText, Loader2,
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import API_BASE_URL from "../config";

export default function VendorPage({ setCurrentPage }) {
    const [vendors, setVendors] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);

    const defaultForm = {
        VendorName: "",
        VendorCode: "",
        Address1: "",
        Address2: "",
        Address3: "",
        ContactPerson: "",
        ContactNo: "",
        FaxNo: "",
        Email: "",
        PaymentTerms: "",
        TIN: "",
        IsActive: 1,
    };

    const [formData, setFormData] = useState(defaultForm);
    const [search, setSearch] = useState("");

    const [showImportModal, setShowImportModal] = useState(false);
    const [importData, setImportData] = useState([]);
    const [importMode, setImportMode] = useState("new");
    const [isImporting, setIsImporting] = useState(false);

    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ─────────────────── API ─────────────────── //

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/Vendor`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            console.log("RAW API RESPONSE:", data[0]);

            const mapped = data.map((v) => ({
                id: v.id ?? v.Id,
                vendorName: v.vendorName ?? v.VendorName ?? "",
                vendorCode: v.vendorCode ?? v.VendorCode ?? "",
                address1: v.address1 ?? v.Address1 ?? "",
                address2: v.address2 ?? v.Address2 ?? "",
                address3: v.address3 ?? v.Address3 ?? "",
                contactPerson: v.contactPerson ?? v.ContactPerson ?? "",
                contactNo: v.contactNo ?? v.ContactNo ?? "",
                faxNo: v.faxNo ?? v.FaxNo ?? "",
                email: v.email ?? v.Email ?? "",
                paymentTerms: v.paymentTerms ?? v.PaymentTerms ?? "",
                tin: v.tin ?? v.TIN ?? "",
                isActive: v.isActive ?? v.IsActive ?? 1,
            }));

            setVendors(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error("Fetch error:", error);
            Swal.fire("Error", `Failed to fetch vendors: ${error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    const createVendor = async () => {
        if (!formData.VendorName.trim()) {
            Swal.fire("Oops!", "Vendor Name is required", "warning");
            return;
        }
        if (!formData.VendorCode.trim()) {
            Swal.fire("Oops!", "Vendor Code is required", "warning");
            return;
        }

        try {
            const payload = { ...formData, IsActive: formData.IsActive ? 1 : 0 };
            console.log("CREATE PAYLOAD:", payload);

            const res = await fetch(`${API_BASE_URL}api/Vendor`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errText = await res.text();
                console.error("Create error response:", errText);
                throw new Error(errText || `HTTP ${res.status}`);
            }

            setOpenCreate(false);
            clearForm();
            fetchVendors();
            Swal.fire("Success!", "Vendor created successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const updateVendor = async () => {
        if (!formData.VendorName.trim()) {
            Swal.fire("Oops!", "Vendor Name is required", "warning");
            return;
        }
        if (!formData.VendorCode.trim()) {
            Swal.fire("Oops!", "Vendor Code is required", "warning");
            return;
        }

        try {
            const payload = { ...formData, IsActive: formData.IsActive ? 1 : 0 };
            console.log("UPDATE PAYLOAD:", payload);

            const res = await fetch(`${API_BASE_URL}api/Vendor/${selectedVendor.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errText = await res.text();
                console.error("Update error response:", errText);
                throw new Error(errText || `HTTP ${res.status}`);
            }

            setOpenEdit(false);
            setSelectedVendor(null);
            clearForm();
            fetchVendors();
            Swal.fire("Updated!", "Vendor updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const deleteVendor = async (vendor) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete ${vendor.vendorName}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE_URL}api/Vendor/${vendor.id}`, {
                    method: "DELETE",
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                fetchVendors();
                Swal.fire("Deleted!", "Vendor has been deleted.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // ─────────────────── EXPORT ─────────────────── //

    const exportCSV = () => {
        const exportData = vendors.map((v) => ({
            VendorName: v.vendorName,
            VendorCode: v.vendorCode,
            Address1: v.address1,
            Address2: v.address2,
            Address3: v.address3,
            ContactPerson: v.contactPerson,
            ContactNo: v.contactNo,
            FaxNo: v.faxNo,
            Email: v.email,
            PaymentTerms: v.paymentTerms,
            TIN: v.tin,
            IsActive: v.isActive,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");
        XLSX.writeFile(workbook, "vendors.xlsx");
    };

    // ─────────────────── IMPORT ─────────────────── //

    const handleImportFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                Swal.fire("Error", "No valid data found in Excel file", "error");
                return;
            }

            const dataWithStatus = jsonData.map((item) => {
                const existing = vendors.find(
                    (v) =>
                        v.vendorCode?.toLowerCase() === item.VendorCode?.toLowerCase() ||
                        (item.Email && v.email?.toLowerCase() === item.Email?.toLowerCase())
                );
                return {
                    ...item,
                    isDuplicate: !!existing,
                    existingId: existing?.id,
                };
            });

            setImportData(dataWithStatus);
            setShowImportModal(true);
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to parse Excel file", "error");
        }

        e.target.value = "";
    };

    const processImport = async () => {
        setShowImportModal(false);
        setIsImporting(true);

        let successCount = 0;
        let failCount = 0;

        for (const item of importData) {
            if (item.isDuplicate && importMode === "new") continue;

            try {
                const payload = {
                    VendorName: item.VendorName || "N/A",
                    VendorCode: item.VendorCode || "N/A",
                    Address1: item.Address1 || "N/A",
                    Address2: item.Address2 || "N/A",
                    Address3: item.Address3 || "N/A",
                    ContactPerson: item.ContactPerson || "N/A",
                    ContactNo: item.ContactNo || "N/A",
                    FaxNo: item.FaxNo || "N/A",
                    Email: item.Email || "N/A",
                    PaymentTerms: item.PaymentTerms || "N/A",
                    TIN: item.TIN || "N/A",
                    IsActive: item.IsActive ?? 1,
                };

                const url = item.isDuplicate && importMode === "update"
                    ? `${API_BASE_URL}api/Vendor/${item.existingId}`
                    : `${API_BASE_URL}api/Vendor`;
                const method = item.isDuplicate && importMode === "update" ? "PUT" : "POST";

                const res = await fetch(url, {
                    method,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                if (res.ok) successCount++;
                else {
                    const errText = await res.text();
                    console.error("Import row failed:", errText);
                    failCount++;
                }
            } catch (err) {
                console.error("Import row error:", err);
                failCount++;
            }
        }

        setIsImporting(false);
        setImportData([]);
        setImportMode("new");
        fetchVendors();

        Swal.fire({
            icon: "success",
            title: "Import Complete!",
            html: `
                <p class="text-lg mb-2"><strong>${successCount}</strong> vendors processed successfully</p>
                ${failCount > 0 ? `<p class="text-red-600">${failCount} failed</p>` : ""}
            `,
        });
    };

    // ─────────────────── EFFECTS ─────────────────── //

    useEffect(() => { fetchVendors(); }, []);

    useEffect(() => {
        if (!search.trim()) {
            setFiltered(vendors);
        } else {
            const term = search.toLowerCase();
            setFiltered(
                vendors.filter(
                    (v) =>
                        v.vendorName?.toLowerCase().includes(term) ||
                        v.vendorCode?.toLowerCase().includes(term) ||
                        v.contactPerson?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, vendors]);

    // ─────────────────── HELPERS ─────────────────── //

    const clearForm = () => setFormData(defaultForm);

    const openEditModal = (v) => {
        setSelectedVendor(v);
        setFormData({
            VendorName: v.vendorName || "",
            VendorCode: v.vendorCode || "",
            Address1: v.address1 || "",
            Address2: v.address2 || "",
            Address3: v.address3 || "",
            ContactPerson: v.contactPerson || "",
            ContactNo: v.contactNo || "",
            FaxNo: v.faxNo || "",
            Email: v.email || "",
            PaymentTerms: v.paymentTerms || "",
            TIN: v.tin || "",
            IsActive: v.isActive ?? 1,
        });
        setOpenEdit(true);
    };

    const duplicateCount = importData.filter((d) => d.isDuplicate).length;
    const newCount = importData.filter((d) => !d.isDuplicate).length;

    // ─────────────────── RENDER ─────────────────── //

    return (
        <div className="min-h-screen from-gray-50 to-gray-100 p-8">

            {/* IMPORT LOADING OVERLAY */}
            {isImporting && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex flex-col justify-center items-center z-[100]">
                    <Loader2 size={56} className="text-white animate-spin mb-4" />
                    <p className="text-white text-xl font-semibold">Importing vendors...</p>
                    <p className="text-white/70 text-sm mt-1">Please wait, do not close this page</p>
                </div>
            )}

            {/* BACK */}
            <button
                onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Store className="text-indigo-600" size={36} />
                            Vendors
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your vendor relationships</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportCSV}
                            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Download size={18} /> Export
                        </button>
                        <label className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
                            <Upload size={18} /> Import
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
                        </label>
                        <button
                            onClick={() => setOpenCreate(true)}
                            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Plus size={20} /> Create Vendor
                        </button>
                    </div>
                </div>
            </div>

            {/* SEARCH */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by vendor name, code, or contact person..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">Vendor Info</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Code</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Contact Person</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Contact Details</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-gray-200" />
                                                <div className="space-y-2">
                                                    <div className="h-3 bg-gray-200 rounded w-32" />
                                                    <div className="h-2 bg-gray-100 rounded w-20" />
                                                </div>
                                            </div>
                                        </td>
                                        {[...Array(4)].map((_, j) => (
                                            <td key={j} className="px-6 py-4">
                                                <div className="h-3 bg-gray-200 rounded w-20" />
                                            </td>
                                        ))}
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                                                <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : paginated.length > 0 ? (
                                paginated.map((vendor, index) => (
                                    <tr key={vendor.id || index} className="hover:bg-indigo-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                                                    {vendor.vendorName?.charAt(0).toUpperCase() || "?"}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-800">{vendor.vendorName || "—"}</div>
                                                    {vendor.address1 && (
                                                        <div className="text-xs text-gray-500">{vendor.address1}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                {vendor.vendorCode || "—"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <User size={16} className="text-gray-400" />
                                                {vendor.contactPerson || "—"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1 text-sm">
                                                {vendor.contactNo && (
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <Phone size={14} className="text-gray-400" />
                                                        {vendor.contactNo}
                                                    </div>
                                                )}
                                                {vendor.email && (
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <Mail size={14} className="text-gray-400" />
                                                        {vendor.email}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                {vendor.isActive === 1 || vendor.isActive === true ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <CheckCircle size={14} /> Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        <XCircle size={14} /> Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(vendor)}
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteVendor(vendor)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Store size={48} />
                                            <p className="text-lg font-medium">No vendors found</p>
                                            <p className="text-sm">Try adjusting your search or create a new vendor</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {!loading && paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} vendors
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* CREATE / EDIT MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Create New Vendor" : "Edit Vendor"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedVendor(null);
                        clearForm();
                    }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
                        <Input label="Vendor Name*" value={formData.VendorName} onChange={(val) => setFormData({ ...formData, VendorName: val })} required />
                        <Input label="Vendor Code*" value={formData.VendorCode} onChange={(val) => setFormData({ ...formData, VendorCode: val })} required />
                        <Input label="Address 1" value={formData.Address1} onChange={(val) => setFormData({ ...formData, Address1: val })} />
                        <Input label="Address 2" value={formData.Address2} onChange={(val) => setFormData({ ...formData, Address2: val })} />
                        <Input label="Address 3" value={formData.Address3} onChange={(val) => setFormData({ ...formData, Address3: val })} />
                        <Input label="Contact Person" value={formData.ContactPerson} onChange={(val) => setFormData({ ...formData, ContactPerson: val })} />
                        <Input label="Contact Number" value={formData.ContactNo} onChange={(val) => setFormData({ ...formData, ContactNo: val })} />
                        <Input label="Fax No" value={formData.FaxNo} onChange={(val) => setFormData({ ...formData, FaxNo: val })} />
                        <Input label="Email" type="email" value={formData.Email} onChange={(val) => setFormData({ ...formData, Email: val })} />
                        <Input label="Payment Terms" value={formData.PaymentTerms} onChange={(val) => setFormData({ ...formData, PaymentTerms: val })} />
                        <Input label="TIN" value={formData.TIN} onChange={(val) => setFormData({ ...formData, TIN: val })} />
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={formData.IsActive === 1 || formData.IsActive === true}
                                onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked ? 1 : 0 })}
                                id="isActive"
                                className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                            <label htmlFor="isActive" className="text-sm font-semibold text-gray-700 cursor-pointer">
                                Active Vendor
                            </label>
                        </div>
                    </div>
                    <button
                        onClick={openCreate ? createVendor : updateVendor}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create Vendor" : "Update Vendor"}
                    </button>
                </Modal>
            )}

            {/* IMPORT PREVIEW MODAL */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-3xl font-bold flex items-center gap-3">
                                        <FileText size={32} /> Import Preview - Vendors
                                    </h2>
                                    <p className="text-indigo-100 mt-2">
                                        Review data before importing • {importData.length} total records
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setShowImportModal(false); setImportData([]); setImportMode("new"); }}
                                    className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-green-100 rounded-full p-3">
                                        <CheckCircle className="text-green-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{newCount}</p>
                                        <p className="text-sm text-gray-600">New Records</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-red-100 rounded-full p-3">
                                        <AlertCircle className="text-red-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{duplicateCount}</p>
                                        <p className="text-sm text-gray-600">Duplicates Found</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mode Selection */}
                        {duplicateCount > 0 && (
                            <div className="px-8 py-4 bg-yellow-50 border-b border-yellow-200">
                                <p className="text-sm font-semibold text-yellow-800 mb-3">
                                    What would you like to do with duplicate records?
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setImportMode("new")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${importMode === "new"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                            }`}
                                    >
                                        Skip Duplicates (Import {newCount} new only)
                                    </button>
                                    <button
                                        onClick={() => setImportMode("update")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${importMode === "update"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                            }`}
                                    >
                                        Update Existing (Import all {importData.length})
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Data Table */}
                        <div className="flex-1 overflow-y-auto px-8 py-4">
                            <table className="w-full">
                                <thead className="sticky top-0 bg-gray-100 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Vendor Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Code</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Contact Person</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Email</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {importData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`${item.isDuplicate ? "bg-red-50 hover:bg-red-100" : "bg-white hover:bg-gray-50"} transition-colors`}
                                        >
                                            <td className="px-4 py-3">
                                                {item.isDuplicate ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                        <AlertCircle size={14} /> Duplicate
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                        <CheckCircle size={14} /> New
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-gray-800">{item.VendorName}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{item.VendorCode || "—"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{item.ContactPerson || "—"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{item.Email || "—"}</td>
                                            <td className="px-4 py-3 text-xs font-semibold">
                                                {item.isDuplicate ? (
                                                    <span className="text-red-600">
                                                        {importMode === "update" ? "Will Update" : "Will Skip"}
                                                    </span>
                                                ) : (
                                                    <span className="text-green-600">Will Create</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={() => { setShowImportModal(false); setImportData([]); setImportMode("new"); }}
                                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={processImport}
                                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                {importMode === "update"
                                    ? `Import All (${importData.length} records)`
                                    : `Import New Only (${newCount} records)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────── COMPONENTS ─────────────────── //

function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}